import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Mail, 
  Monitor,
  Shield,
  Cloud,
  Network,
  Headphones,
  Cpu,
  Settings,
  Eye,
  FileText
} from 'lucide-react';

const DAVID_PHOTO = '/assets/david-raymond.jpg';
const ALFRED_PHOTO = '/assets/alfred-arnold.jpg';
const JESSICA_PHOTO = '/assets/jessica-raymond.jpg';
const JEFF_PHOTO = '/assets/jeff-keizer.jpg';

const whatWeDo = [
  { icon: Monitor, text: 'Sell and deploy business hardware and software' },
  { icon: Settings, text: 'Run managed IT operations with monitoring and patching' },
  { icon: Shield, text: 'Improve cybersecurity across endpoints, identity, email, and backups' },
  { icon: Cloud, text: 'Administer Microsoft 365 and support Azure deployments' },
  { icon: Network, text: 'Design and support networks, firewalls, Wi-Fi, VLANs, and VPNs' },
  { icon: Headphones, text: 'Support business voice systems and user enablement' },
  { icon: Cpu, text: 'Deliver AI and OCR automation with defined outcomes' }
];

const tooling = [
  { icon: Eye, text: 'Monitoring and patching with N-able N-central' },
  { icon: Shield, text: 'Endpoint security reporting with SentinelOne EDR' },
  { icon: FileText, text: 'Documented standards and repeatable onboarding and offboarding' }
];

const AboutPage = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e) => setIsMobile(!e.matches);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  return (
    <div className="relative overflow-hidden" data-testid="about-page">
      {/* Hero Section - About NPM Intro */}
      <section className="relative hero-gradient section-padding-hero" data-testid="about-hero">
        <div className="abstract-shape abstract-shape-1"></div>
        
        <div className="site-container relative">
          <div className="max-w-3xl prose-width">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3f3f3f] leading-[1.15] mb-5">
              About NPM
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              NPM provides business technology sales, deployment, managed IT, and cybersecurity support for organizations across Las Vegas and Henderson. The focus is stable systems, predictable support, and security-first standards.
            </p>
            <p className="text-base font-medium text-[#7dc242] mt-4">
              Serving Southern Nevada since 1999.
            </p>
          </div>
        </div>
      </section>

      {/* Meet David Raymond Section */}
      <section className="section-padding bg-white" data-testid="david-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Photo - Left */}
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] max-w-[280px] mx-auto lg:mx-0 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <img 
                  src={DAVID_PHOTO} 
                  alt="David Raymond, President and CEO"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            {/* Bio - Right */}
            <div className="lg:col-span-8">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-2">
                Meet David Raymond
              </h2>
              <p className="text-[#7dc242] font-medium mb-4">President and CEO</p>
              
              <div className="space-y-4 text-[0.9375rem] text-gray-600 leading-relaxed">
                <p>
                  David Raymond is President and CEO of NPM Computer Sales, Inc. Since 2002, he has led company operations delivering computer sales and services including network builds, workstation and server deployments, and remote services ranging from application training to systems management. NPM holds partner designations with Microsoft, Intel, and Xerox. NPM is expanding services to include web development, domain procurement, and Microsoft SharePoint implementation.
                </p>
                <p>
                  Prior to NPM, David built and scaled multiple technology ventures. In 1991, he co-founded Renie and Raymond, Inc., developing a patented system that enabled theme parks to capture, digitize, and sell ride videos to guests. The system was later contracted to Busch Gardens in Florida. Earlier versions of the technology supported contracts with the U.S. Air Force, the Department of the Interior, and several Las Vegas area hotels.
                </p>
                <p>
                  In 1982, David founded Elsafe Nevada, Inc., importing and distributing electronic hotel safes for the Nevada hotel industry. He pioneered telemetry systems that transmitted safe door status to a central computer at hotel front desks, helping hotels substantiate insurance claims tied to forcible entry. He placed more than 10,000 safes in Las Vegas before selling the company to the Elsafe factory in Norway in 1991.
                </p>
                <p>
                  David earned a Bachelor of Science in Business Administration from Arizona State University in 1978. He has spent extensive time overseas in Spain, Italy, Wales, and England. He is married with two daughters and lives in Las Vegas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alfred Arnhold Section */}
      <section className="section-padding grid-pattern" data-testid="alfred-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Photo - Left */}
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] max-w-[280px] mx-auto lg:mx-0 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <img 
                  src={ALFRED_PHOTO} 
                  alt="Alfred Arnhold, Technical Services Specialist"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            {/* Bio - Right */}
            <div className="lg:col-span-8">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-2">
                Alfred Arnhold
              </h2>
              <p className="text-[#7dc242] font-medium mb-4">Technical Services Specialist</p>
              
              <div className="space-y-4 text-[0.9375rem] text-gray-600 leading-relaxed">
                <p>
                  Alfred Arnhold is a seasoned electronics and technical services professional with more than 44 years of hands-on experience in system integration, infrastructure support, installation, and repair. He brings deep practical knowledge to NPM, where he helps clients maintain and optimize computer systems, network infrastructure, and telecommunications solutions.
                </p>
                <p>
                  Alfred's interest in electronics began early and developed into a lifelong career. By age 17, he was working as a bench technician for a high-end electronics retailer, diagnosing and repairing a wide range of equipment. He later expanded into custom home theater, commercial installations, and corporate boardroom technology systems.
                </p>
                <p>
                  His background also includes field service and infrastructure work with a major cable provider, where he maintained feeder lines and supported residential customers. After returning to his technical roots, Alfred built and operated his own business for more than two decades, providing custom electronics installations and technical services for homes and businesses.
                </p>
                <p>
                  Today, Alfred's work with NPM builds on a professional relationship spanning more than 20 years. He is known for practical problem-solving, reliability, and a longstanding commitment to quality service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jessica Raymond Section */}
      <section className="section-padding bg-white" data-testid="jessica-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Photo - Left */}
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] max-w-[280px] mx-auto lg:mx-0 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <img 
                  src={JESSICA_PHOTO} 
                  alt="Jessica Raymond, Controller"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            {/* Bio - Right */}
            <div className="lg:col-span-8">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-2">
                Jessica Raymond
              </h2>
              <p className="text-[#7dc242] font-medium mb-4">Controller</p>
              
              <div className="space-y-4 text-[0.9375rem] text-gray-600 leading-relaxed">
                <p>
                  Jessica Raymond is the Controller at NPM. She earned a Bachelor of Science in Business Administration from Rocky Mountain College in 2015 and graduated Cum Laude. Her studies emphasized small business management with a minor in organizational leadership. She attended Nevada State High School, a dual enrollment charter school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jeff Keizer Section - Creative Partner */}
      <section className="section-padding grid-pattern" data-testid="jeff-section">
        <div className="site-container">
          {/* Partnership Intro */}
          <div className="max-w-3xl mb-10 lg:mb-12">
            <p className="text-[0.9375rem] text-gray-600 leading-relaxed italic border-l-4 border-[#7dc242] pl-4">
              NPM partners with Jeff Keizer of Keizer Design to provide web design, content development, brand presentation, and creative direction for clients who need a stronger digital presence. This partnership adds strategic creative support to NPM's core technical services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Photo - Left */}
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] max-w-[280px] mx-auto lg:mx-0 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <img 
                  src={JEFF_PHOTO} 
                  alt="Jeff Keizer, Creative Director"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            {/* Bio - Right */}
            <div className="lg:col-span-8">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-2">
                Jeff Keizer
              </h2>
              <p className="text-[#7dc242] font-medium">Creative Director, Keizer Design</p>
              <p className="text-[#7D77B7] font-medium mb-4">Creative Services Partner</p>
              
              <div className="space-y-4 text-[0.9375rem] text-gray-600 leading-relaxed">
                <p>
                  Jeff Keizer is the Creative Director of Keizer Design, where he helps businesses strengthen their brand presence through brand development, corporate identity, web design, and visual communication. He brings a practical, strategic, client-focused approach shaped by years of experience in marketing, editorial design, and digital branding. Through his collaboration with NPM, Jeff helps clients turn technical capability into clear, effective, professional online experiences.
                </p>
                <p className="text-[#3f3f3f]">
                  <a href="mailto:jeff@keizerdesign.com" className="hover:text-[#7dc242] transition-colors">jeff@keizerdesign.com</a>
                  <span className="mx-2">|</span>
                  <a href="tel:+17022806921" className="hover:text-[#7dc242] transition-colors">702-280-6921</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section 
        className="section-padding section-bg-wrapper mobile-bg-purple" 
        data-testid="what-we-do-section"
      >
        {/* Background layer */}
        {!isMobile && (
          <div 
            className="section-bg-layer"
            style={{ backgroundImage: 'url(/assets/motherboard-bg-4k.png)' }}
            aria-hidden="true"
          />
        )}
        <div className="site-container section-bg-content">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              What We Do
            </h2>
            <p className="text-lg lg:text-xl text-black max-w-2xl mx-auto">
              Comprehensive IT services tailored to your business needs
            </p>
          </div>
          
          {/* Grid matching Home Core Services - 3 columns desktop */}
          <div className="card-grid-services">
            {whatWeDo.map((item, index) => (
              <div 
                key={index} 
                className="npm-card service-card p-5 lg:p-6"
                data-testid={`what-we-do-${index}`}
              >
                <div className="npm-card-icon mb-4">
                  <item.icon className="w-6 h-6" />
                </div>
                <p className="text-[#3f3f3f] text-[0.9375rem] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tooling & Visibility Section */}
      <section className="section-padding grid-pattern" data-testid="tooling-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Monitoring workstation image */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-xl overflow-hidden shadow-lg border border-gray-200">
                <img 
                  src="/assets/it-monitoring-workstation.png"
                  alt="IT monitoring workstation with dashboards showing system health, network status, and endpoint protection"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-5">
                Tooling & Visibility
              </h2>
              <p className="text-lg lg:text-xl text-gray-500 mb-6">
                We use industry-leading tools to ensure comprehensive monitoring, security, and documentation.
              </p>
              <ul className="space-y-4">
                {tooling.map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`tooling-${index}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#7D77B7]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-[18px] h-[18px] text-[#7D77B7]" />
                    </div>
                    <span className="text-[#3f3f3f] text-[0.9375rem] pt-2">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section" data-testid="about-cta">
        <div className="site-container text-center">
          <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
            Let's Work Together
          </h2>
          <p className="text-lg lg:text-xl text-gray-500 mb-6 max-w-2xl mx-auto">
            Contact us to discuss how NPM can support your business technology needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-lg hover:-translate-y-0.5 text-white border-0 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="about-cta-phone"
            >
              <a href="tel:+17027695422">
                <Phone className="w-[18px] h-[18px] mr-2" />
                Call (702) 769-5422
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild 
              className="border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-[#7dc242]/5 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="about-cta-email"
            >
              <a href="mailto:david@applevar.com">
                <Mail className="w-[18px] h-[18px] mr-2" />
                david@applevar.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
